/*
================================================================================
FILE HEADER — apps/studio-web/src/lib/runnerBridge.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Option B — per-step      | Created — browser-   |
|    |            |                   | screenshots for Playwright| side WebSocket client|
|    |            |                   | runs                     | for CLI relay        |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- RelayMessage     — union of all messages Studio receives from the CLI relay
- RelayStatus      — connection state machine
- RunnerBridge     — WS client class that connects to the CLI relay server

IMPORTANT POINTS
----------------
1. Uses the native browser WebSocket API (no npm package needed).
2. Default relay URL: ws://localhost:7700
3. Auto-reconnect: retries 3 times with exponential backoff when the server
   closes unexpectedly (e.g. plex run restarts between tests).
4. RelayStatus drives the Connect/Connecting/Disconnect button in ExecutionPanel.
5. When `RELAY_STEP_SCREENSHOT` arrives, the dataUrl is a `data:image/png;base64,...`
   string that can be set directly as <img src> or canvas drawImage().
6. The bridge is a CLASS (not a React hook) so it lives in a ref in App.tsx
   and is not recreated on re-renders.
================================================================================
*/

export type RelayStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export type RelayMessage =
  | {
      type:       'RELAY_RUN_STARTED';
      testName:   string;
      stepCount:  number;
      browser:    string;
      headless:   boolean;
    }
  | {
      type:       'RELAY_STEP_STARTED';
      stepId:     string;
      stepIndex:  number;
      total:      number;
      stepType:   string;
      label:      string;
    }
  | {
      type:       'RELAY_STEP_SCREENSHOT';
      stepId:     string;
      stepIndex:  number;
      total:      number;
      dataUrl:    string;
    }
  | {
      type:        'RELAY_STEP_COMPLETED';
      stepId:      string;
      stepIndex:   number;
      total:       number;
      status:      'ok' | 'not_found' | 'error';
      durationMs:  number;
      matchedBy?:  string;
    }
  | {
      type:        'RELAY_RUN_COMPLETED';
      status:      'passed' | 'failed' | 'passed_healed';
      passedCount: number;
      failedCount: number;
      durationMs:  number;
    };

type StatusListener = (status: RelayStatus) => void;
type MessageListener = (msg: RelayMessage) => void;

export class RunnerBridge {
  private ws: WebSocket | null = null;
  private statusListeners:  StatusListener[]  = [];
  private messageListeners: MessageListener[] = [];
  private _status: RelayStatus = 'disconnected';
  private retries = 0;
  private maxRetries = 3;
  private url = 'ws://localhost:7700';

  get status(): RelayStatus { return this._status; }

  // Subscribe to status changes — returns unsubscribe fn
  onStatus(fn: StatusListener): () => void {
    this.statusListeners.push(fn);
    return () => { this.statusListeners = this.statusListeners.filter(f => f !== fn); };
  }

  // Subscribe to relay messages — returns unsubscribe fn
  on(fn: MessageListener): () => void {
    this.messageListeners.push(fn);
    return () => { this.messageListeners = this.messageListeners.filter(f => f !== fn); };
  }

  connect(url = 'ws://localhost:7700'): void {
    this.url = url;
    this.retries = 0;
    this._doConnect();
  }

  disconnect(): void {
    this.retries = this.maxRetries + 1; // prevent auto-reconnect
    this.ws?.close();
    this._setStatus('disconnected');
  }

  private _doConnect(): void {
    if (this._status === 'connected' || this._status === 'connecting') return;
    this._setStatus('connecting');

    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this._setStatus('error');
      return;
    }

    this.ws.onopen = () => {
      this.retries = 0;
      this._setStatus('connected');
    };

    this.ws.onmessage = (e: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(e.data) as RelayMessage;
        for (const fn of this.messageListeners) fn(msg);
      } catch { /* malformed JSON — ignore */ }
    };

    this.ws.onerror = () => {
      this._setStatus('error');
    };

    this.ws.onclose = () => {
      if (this._status !== 'disconnected') {
        this._setStatus('disconnected');
      }
      // Auto-reconnect with backoff if plex run is still expected to be running
      if (this.retries < this.maxRetries) {
        this.retries++;
        const delay = Math.min(1000 * this.retries, 3000);
        setTimeout(() => { if (this._status === 'disconnected') this._doConnect(); }, delay);
      }
    };
  }

  private _setStatus(s: RelayStatus): void {
    this._status = s;
    for (const fn of this.statusListeners) fn(s);
  }
}
